<template>
    <!-- mobile -->
    <div
        class="min-h-[96px] h-[96px] flex lg:cz-hidden mx-[12.79px] lg:px-0"
        style="transform: translateZ(0);"
    >
        <swiper
            :space-between="40"
            :modules="modules"
            :observer="true"
            :allow-slide-next="true"
            navigation
            @swiper="setSwiperInstance"
            @slide-change="handleSlideChange"
            v-on:slide-next-transition-end="enableNextStep"
            v-on:slide-prev-transition-end="enablePreviousStep"
            v-on:observer-update="updateSwiper"
            class="relative z-0 flex-grow py-3"
        >
            <swiper-slide
                class="flex items-center"
                v-for="(group, index) in visibleGroupsForMobile"
                :key="group"
            >
                <swiper-list-item
                    @goToPreviousSlide="goToPreviousSlide"
                    @goToNextSlide="goToNextSlide"
                    :item-data="group"
                    :is-first-slide="index === 0"
                    :is-last-slide="index === visibleGroupsForMobile.length - 1"
                    role="button"
                    @click="openGroupOverlay(group)"
                    class="h-full"
                />
                <!-- <img
                    src="../assets/images/icons/item-arrow.svg"
                    width="9"
                    height="16"
                    alt="Right arrow"
                    class="transition-transform duration-300 ease-in-out h-[16px] w-[9.1px]"
                    @click="openGroupOverlay(group)"
                > -->
            </swiper-slide>
        </swiper>
    </div>

    <!-- desktop -->
    <base-overlay
        :is-open="showGroupOverlay"
        :show-color-controls="shouldShowColorControls"
        @overlayClose="handleCloseOverlay"
    >
        <template v-slot:header>
            <focus-lock :disabled="hasOpenModal || hasOpenNestedPanel || !showGroupOverlay" group="side-panel" class="w-full">
                <div class="flex w-full justify-between items-center relative font-oceanside">
                    <reset-panel-button
                        class="w-[18px] absolute left-0"
                        v-if="!shouldShowSummary"
                    />
                    <h1
                        v-if="shouldShowSummary"
                        class="flex-1 text-center text-base font-semibold"
                    >
                        {{ $t('SUMMARY') }}
                    </h1>
                    <h1
                        v-else-if="currentGroup"
                        class="flex-1 text-center text-base -mr-[19px] pointer-events-none flex flex-col"
                    >
                        <div class="font-semibold">{{ currentGroup }}</div>
                    </h1>
                    <close-button
                        width="18.84"
                        @click="handleCloseOverlay"
                        tabindex="-1"
                    />
                </div>
            </focus-lock>
        </template>

        <template v-slot:content>
            <focus-lock :disabled="hasOpenModal || hasOpenNestedPanel || !showGroupOverlay" group="side-panel"
                class="content-focus-lock flex flex-col flex-1 min-h-0"
            >
                <step-options
                    v-if="currentGroup || shouldShowSummary"
                    :item-data="currentGroupActual || summaryData"
                />
                <step-option-list v-else
                />
            </focus-lock>
        </template>
    </base-overlay>
</template>

<script>
    /* eslint-disable import/no-unresolved */
    /* eslint-disable no-unused-vars */
    import FocusLock from 'vue-focus-lock';
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { Navigation } from 'swiper';
    import 'swiper/css';

    import allStores from '../stores/all';
    import * as constants from '../constants';

    import SwiperListItem from './SwiperListItem.vue';
    import BaseOverlay from './BaseOverlay.vue';
    import StepOptions from './StepOptions.vue';
    import StepOptionList from './StepOptionList.vue';
    import CloseButton from './buttons/CloseButton.vue';
    import ResetPanelButton from './buttons/ResetPanelButton.vue';

    export default {
        mixins: [allStores],

        emits: ['overlayClose', 'toggle-should-show-summary'],

        components: {
            ResetPanelButton,
            StepOptionList,
            Swiper,
            SwiperSlide,
            SwiperListItem,
            BaseOverlay,
            StepOptions,
            CloseButton,
            FocusLock,
        },

        props: {
            slideProgress: {
                type: Number,
                required: true,
            },
        },

        data() {
            return {
                modules: [Navigation],
                nextButton: null,
                isOverlayCloseInProgress: false,
                swiperInstance: null,
            };
        },

        computed: {
            summaryData() {
                return {
                    name: 'Summary',
                    custom: {
                        'v-group-type': 'summary?',
                    },
                };
            },

            hasOpenNestedPanel() {
                return this.appStore.hasOpenNestedPanel;
            },

            hasOpenModal() {
                return this.appStore.hasOpenModal;
            },

            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            visibleGroupsForMobile() {
                return this.customizerStore.buildVisibleGroups({
                    sourceGroups: this.visibleGroups,
                });
            },

            currentGroup() {
                return this.customizerStore.currentGroup;
            },

            currentGroupActual() {
                // Face / Top / Back are sort of fake groups.

                // Handle integrated Face/Sole panel special case.
                if (this.currentGroup === 'Character Stamping'
                    || this.currentGroup === 'Face/Sole'
                    || this.currentGroup === 'Stamps') {
                    const characterStampingGroup = this.visibleGroups.find((group) => group.name === 'Character Stamping');
                    const stampsGroup = this.visibleGroups.find((group) => group.name === 'Stamps');

                    const subGroups = {};

                    if (characterStampingGroup) {
                        subGroups.characterStamping = {
                            name: 'Character Stamping',
                            code: 'Character Stamping',
                            placements: characterStampingGroup.placements,
                        };
                    }

                    if (stampsGroup) {
                        subGroups.stamps = {
                            name: 'Stamps',
                            code: 'Stamps',
                            placements: stampsGroup.placements,
                        };
                    }

                    return {
                        name: 'Face/Sole',
                        code: 'Face/Sole',
                        custom: {
                            'v-group-type': 'front',
                        },
                        subGroups,
                    };
                }

                if (this.currentGroup === 'Top') {
                    return {
                        name: 'Top',
                        code: 'Top',
                        custom: {
                            'v-group-type': 'top',
                        },
                    };
                }

                if (this.currentGroup === 'Back') {
                    return {
                        name: 'Back',
                        code: 'Back',
                        custom: {
                            'v-group-type': 'back',
                        },
                    };
                }

                return this.visibleGroups.find((group) => group.name === this.currentGroup);
            },

            hasBeenSeen() {
                return this.customizerStore.groupsSeen.includes(this.currentGroup);
            },

            selectionWasMade() {
                return this.customizerStore.selectionsMade.includes(this.currentGroup);
            },

            allowNextSlide() {
                return this.selectionWasMade && this.hasBeenSeen;
            },

            isReset() {
                return this.appStore.currentStep === constants.STEP_RESET;
            },

            isMobile() {
                return this.appStore.isMobile;
            },

            shouldShowColorControls() {
                const groupType = this.currentGroupActual?.custom?.['v-group-type'];

                return this.customizerStore.shouldShowColorControls(groupType);
            },

            showGroupOverlay() {
                return this.appStore.showGroupOverlay;
            },

            shouldShowSummary() {
                return this.appStore.shouldShowSummary;
            },
        },

        watch: {
            selectionWasMade() {
                this.toggleNextButton();
            },

            hasBeenSeen() {
                this.toggleNextButton();
            },

            slideProgress() {
                this.triggerNextSlide();
            },
        },

        mounted() {
            this.nextButton = document.getElementsByClassName('swiper-button-next')[0];
            if (this.nextButton) {
                this.nextButton.addEventListener('click', this.checkForErrors);
            }
            this.toggleNextButton();
        },

        beforeUnmount() {
            if (this.nextButton) {
                this.nextButton.removeEventListener('click', this.checkForErrors);
            }
        },

        methods: {
            setShouldShowSummary(value) {
                this.appStore.setShouldShowSummary(value);
            },

            async handleCloseOverlay() {
                if (this.isOverlayCloseInProgress) { return; }
                this.isOverlayCloseInProgress = true;

                // If a nested panel is open, close it first and do not close the group overlay.
                if (this.appStore.hasOpenNestedPanel) {
                    this.appStore.closeNestedPanel();
                    setTimeout(() => {
                        this.isOverlayCloseInProgress = false;
                    }, 300);
                    return;
                }

                this.setShowGroupOverlay(false);
                this.$emit('close-overlay');
                await new Promise((resolve) => {
                    setTimeout(resolve, 250);
                });
                this.setShouldShowSummary(false);
                this.isOverlayCloseInProgress = false;
            },

            toggleShouldShowSummary() {
                this.$emit('toggle-should-show-summary');
            },

            setSelectedGroup(itemData) {
                this.customizerStore.setCurrentGroup(itemData.name);
            },

            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            openGroupOverlay(group) {
                if (this.customizerStore.isSurefitHubLocked(group.placements)) {
                    return;
                }

                this.setSelectedGroup(group);
                this.customizerStore.addGroupToSeen(group.name);
                this.setShowGroupOverlay(true);
            },

            enableNextStep() {
                this.customizerStore.enableNextGroup();
            },

            enablePreviousStep() {
                this.customizerStore.enablePreviousGroup();
            },

            toggleNextButton() {
                if (!this.nextButton) {
                    return;
                }

                if (this.allowNextSlide) {
                    this.nextButton.classList.remove('custom-disabled');
                } else {
                    this.nextButton?.classList?.add('custom-disabled');
                }
            },

            triggerNextSlide() {
                if (this.nextButton) {
                    this.nextButton.click();
                }
            },

            goToPreviousSlide() {
                if (!this.swiperInstance || this.swiperInstance.isBeginning) {
                    return;
                }

                this.swiperInstance.slidePrev();
            },

            goToNextSlide() {
                if (!this.swiperInstance || this.swiperInstance.isEnd) {
                    return;
                }

                this.swiperInstance.slideNext();
            },

            setSwiperInstance(swiper) {
                this.swiperInstance = swiper;
            },

            handleSlideChange(swiper) {
            },

            updateSwiper(swiper) {
                if (this.isReset) {
                    setTimeout(() => {
                        swiper.slideTo(0, 300, false);
                    }, '300');
                } else {
                    let currentGroupName = this.currentGroup;

                    if (currentGroupName === 'Character Stamping' || currentGroupName === 'Stamps') {
                        currentGroupName = 'Face/Sole';
                    }

                    const currentGroupIndex = this.visibleGroupsForMobile.findIndex((group) => group.name === currentGroupName);

                    swiper.slideTo(currentGroupIndex, 300, false);
                    this.toggleNextButton();
                }
            },
        },
    };
</script>

<style>
    #cz3 .swiper-button-prev,
    #cz3 .swiper-button-next {
        display: none;
        /* z-index: 10;
        position: absolute;
        top: 55%;
        transform: translateY(-50%);
        display: block;
        align-items: center;
        justify-content: center;
        padding-top: 32px;
        width: 11.21px;
        height: 100%;
        color: black; */
    }

    #cz3 .swiper-slide {
        padding-left: 24px;
        padding-right: 24px;
    }

    #cz3 .swiper-button-prev {
        left: 4;
    }

    #cz3 .swiper-button-disabled {
        opacity: 0.2;
    }

    #cz3 .swiper-button-prev::after {
        content: url(@/assets/images/icons/arrow.svg);
        height: 20px;
        display: inline-block;
        transform: rotate(180deg);
    }

    #cz3 .swiper-button-next {
        right: 0;
    }

    #cz3 .swiper-button-next::after {
        content: url(@/assets/images/icons/arrow.svg);
        height: 20px;
    }

    /* Make focus-lock's inner [data-lock] div participate in the flex layout chain
       so the Apply Selections CTA stays pinned to the bottom of the panel. */
    #cz3 .content-focus-lock > [data-lock] {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }
</style>
